import { useEffect, useMemo, useState } from "react";
import { ShieldCheck, AlertTriangle, CheckCircle2, Lock } from "lucide-react";
import { useLocale } from "../../../context/LanguageContext";
import { toBcp47 } from "../../../i18n";
import {
  useOrganizationSsoQuery,
  useUpdateOrganizationSsoMutation,
} from "../../../graphql/generated";
import { ssoFragmentToFormState } from "../types";
import { SSOForm } from "./SSOForm";

/** Per-provider feature flags from Vite env. */
function readProviderFlags() {
  const env = import.meta.env;
  return {
    google: env.VITE_OIDC_PROVIDER_GOOGLE_ENABLED === "true",
    microsoft: env.VITE_OIDC_PROVIDER_MICROSOFT_ENABLED === "true",
  };
}

type SSOStatus = "disabled" | "enabled" | "enforced";

function computeStatus(enabled: boolean, enforced: boolean): SSOStatus {
  if (enforced) return "enforced";
  if (enabled) return "enabled";
  return "disabled";
}

interface OrganizationSSOPanelProps {
  organizationId: string;
}

/**
 * Data-loading + form orchestration for the Organization SSO settings UI.
 *
 * Used by:
 *  - `OrganizationSSOPage` — org-scoped page for ORG_ADMIN at `/:orgSlug/sso`
 *  - `EditOrganizationDrawer` SSO tab — platform-ADMIN flow at `/admin/organizations`
 *
 * Backend authz: `updateOrganizationSSO` accepts both ADMIN and ORG_ADMIN; the
 * `Organization.sso` field resolver returns `null` for any other caller (we treat
 * that as "no permission" and render an empty state).
 */
export function OrganizationSSOPanel({
  organizationId,
}: OrganizationSSOPanelProps) {
  const { t } = useLocale();
  const [toast, setToast] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);
  const [serverError, setServerError] = useState<string | undefined>();

  const isFeatureFlagOn = import.meta.env.VITE_FEATURE_SSO_OIDC === "true";
  const providerFlags = readProviderFlags();

  const { data, loading, error, refetch } = useOrganizationSsoQuery({
    variables: { id: organizationId },
    skip: !organizationId || !isFeatureFlagOn,
    fetchPolicy: "cache-and-network",
  });

  const [updateSSO, { loading: saving }] = useUpdateOrganizationSsoMutation();

  // Auto-dismiss toast.
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 5000);
    return () => clearTimeout(timer);
  }, [toast]);

  const initialState = useMemo(
    () => ssoFragmentToFormState(data?.organization?.sso ?? null),
    [data?.organization?.sso],
  );

  // ── Defence-in-depth gates ─────────────────────────────────────────────────
  if (!isFeatureFlagOn) {
    return (
      <EmptyState
        icon={<Lock className="h-5 w-5" />}
        title={t("sso.empty.featureOffTitle")}
        body={t("sso.empty.featureOffBody")}
      />
    );
  }

  if (loading && !data) {
    return (
      <div
        role="status"
        aria-label={t("common.loading")}
        className="flex items-center justify-center py-16"
      >
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  if (error) {
    return (
      <EmptyState
        icon={<AlertTriangle className="h-5 w-5 text-destructive" />}
        title={t("common.error")}
        body={error.message}
        action={
          <button
            type="button"
            onClick={() => refetch()}
            className="h-9 rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            {t("common.refresh")}
          </button>
        }
      />
    );
  }

  // Server returned `sso: null` despite query succeeding — caller is not an
  // admin of this org. Bail out without rendering tenant IDs / domains.
  if (data && !data.organization?.sso) {
    return (
      <EmptyState
        icon={<Lock className="h-5 w-5" />}
        title={t("sso.empty.notAdminTitle")}
        body={t("sso.empty.notAdminBody")}
      />
    );
  }

  const status = computeStatus(initialState.enabled, initialState.enforced);

  return (
    <div className="space-y-5">
      <StatusBanner
        status={status}
        updatedAt={data?.organization?.sso?.updatedAt}
      />

      {toast && (
        <div
          role="status"
          className={`flex items-center gap-2 rounded-md border px-4 py-2 text-sm ${
            toast.type === "success"
              ? "border-success/30 bg-success/10 text-success"
              : "border-destructive/30 bg-destructive/10 text-destructive"
          }`}
        >
          {toast.type === "success" ? (
            <CheckCircle2 className="h-4 w-4" />
          ) : (
            <AlertTriangle className="h-4 w-4" />
          )}
          {toast.message}
        </div>
      )}

      <SSOForm
        organizationId={organizationId}
        initial={initialState}
        isSaving={saving}
        serverError={serverError}
        providerFlags={providerFlags}
        onSubmit={async (input) => {
          setServerError(undefined);
          try {
            await updateSSO({ variables: { input } });
            setToast({ type: "success", message: t("sso.toast.saved") });
          } catch (e) {
            const message =
              e instanceof Error ? e.message : t("sso.toast.saveFailed");
            setServerError(message);
            setToast({ type: "error", message });
          }
        }}
      />
    </div>
  );
}

// ── Internal building blocks ───────────────────────────────────────────────

function EmptyState({
  icon,
  title,
  body,
  action,
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-3 rounded-lg border border-border bg-card px-6 py-12 text-center">
      <div className="rounded-full bg-muted p-2 text-muted-foreground">
        {icon}
      </div>
      <h2 className="text-base font-semibold text-foreground">{title}</h2>
      <p className="max-w-md text-sm text-muted-foreground">{body}</p>
      {action}
    </div>
  );
}

function StatusBanner({
  status,
  updatedAt,
}: {
  status: SSOStatus;
  updatedAt?: string | null;
}) {
  const { t, locale } = useLocale();
  const palette: Record<SSOStatus, string> = {
    disabled: "border-border bg-muted text-muted-foreground",
    enabled: "border-success/30 bg-success/10 text-success",
    enforced: "border-warning/30 bg-warning/10 text-warning",
  };

  const formatted = updatedAt
    ? new Date(updatedAt).toLocaleString(toBcp47(locale), {
        dateStyle: "medium",
        timeStyle: "short",
      })
    : null;

  return (
    <div
      className={`flex items-center justify-between gap-3 rounded-lg border px-4 py-3 ${palette[status]}`}
    >
      <div className="flex items-center gap-2">
        <ShieldCheck className="h-4 w-4" />
        <span className="text-sm font-medium">
          {t(`sso.status.${status}`)}
        </span>
      </div>
      {formatted && (
        <span className="text-xs">
          {t("sso.status.updatedAt", { timestamp: formatted })}
        </span>
      )}
    </div>
  );
}
